import React, { useState } from 'react';
import { slugify, POST_CATEGORIES, PlusIconPath } from './constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import ImageUpload, { resolveImageUrl } from './ImageUpload';

interface Props {
  data: any;
  onChange: (data: any) => void;
  websiteId?: string;
  domain?: string;
}

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const NewspaperIcon: React.FC<{ className?: string }> = ({ className = 'w-10 h-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

const PostsEditor: React.FC<Props> = ({ data, onChange, websiteId, domain }) => {
  const siteBaseUrl = domain ? `https://${domain}` : undefined;
  const form = data || { posts: [] };
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const emit = (newForm: any) => onChange(newForm);

  const updatePost = (idx: number, field: string, value: any) => {
    const posts = [...form.posts];
    posts[idx] = { ...posts[idx], [field]: value };
    if (field === 'title') {
      posts[idx].slug = slugify(value);
    }
    emit({ ...form, posts });
  };

  const addPost = () => {
    const today = new Date().toISOString().slice(0, 10);
    const posts = [...(form.posts || []), {
      slug: 'new-post',
      title: 'New Post',
      excerpt: '',
      content: '',
      date: today,
      category: '',
    }];
    emit({ ...form, posts });
    setExpandedIdx(posts.length - 1);
  };

  const removePost = (idx: number) => {
    const posts = [...form.posts];
    posts.splice(idx, 1);
    emit({ ...form, posts });
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-display font-semibold text-primary">Blog Posts</h2>
          <p className="text-sm text-muted mt-1">{form.posts?.length || 0} posts (3-5 required)</p>
        </div>
        <button onClick={addPost} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium hover:bg-accent/20 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
          </svg>
          Add Post
        </button>
      </div>

      {(!form.posts || form.posts.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <NewspaperIcon className="w-10 h-10 text-zinc-300 mb-3" />
          <p className="text-sm text-muted mb-4">No blog posts yet</p>
          <button onClick={addPost} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors">
            Add your first post
          </button>
        </div>
      )}

      {(form.posts || []).map((post: any, idx: number) => (
        <div
          key={idx}
          data-editor-key={`posts.${idx}`}
          data-editor-expanded={expandedIdx === idx ? 'true' : 'false'}
          tabIndex={-1}
          className="border border-zinc-200 rounded-lg overflow-hidden bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <button
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            className="w-full px-4 py-3 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              {post.image ? (
                <img
                  src={resolveImageUrl(post.image, siteBaseUrl)}
                  alt=""
                  className="w-10 h-10 rounded object-cover shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="w-10 h-10 rounded bg-zinc-200 shrink-0" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-primary truncate">{post.title || `Post ${idx + 1}`}</span>
                {post.slug && <span className="text-xs text-muted font-mono shrink-0">/{post.slug}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {post.category && <span className="text-xs bg-zinc-100 text-muted px-2 py-0.5 rounded">{post.category}</span>}
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); removePost(idx); }}
                className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                title="Remove post"
              >
                <TrashIcon />
              </span>
              <svg className={`w-4 h-4 text-muted transition-transform ${expandedIdx === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </button>

          {expandedIdx === idx && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input value={post.title || ''} onChange={(e) => updatePost(idx, 'title', e.target.value)} />
                  <p className="text-xs text-muted mt-1">Slug: <span className="font-mono">{post.slug || '—'}</span></p>
                </div>
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="flex h-10 w-full items-center justify-between rounded-md border border-zinc-200 bg-zinc-100/70 px-3 py-2 text-sm text-zinc-900 transition-colors hover:bg-zinc-100">
                        <span>{post.date ? format(parseISO(post.date), 'MMM d, yyyy') : 'Pick a date'}</span>
                        <CalendarIcon className="w-4 h-4 text-muted" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={post.date ? parseISO(post.date) : undefined}
                        onSelect={(date) => {
                          if (date) updatePost(idx, 'date', format(date, 'yyyy-MM-dd'));
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={post.category || undefined} onValueChange={(v) => updatePost(idx, 'category', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {POST_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {websiteId && (
                <div className="space-y-1.5">
                  <Label>Cover Image</Label>
                  <ImageUpload
                    websiteId={websiteId}
                    currentUrl={post.image || undefined}
                    path="posts"
                    onUploaded={(url) => updatePost(idx, 'image', url)}
                    onRemoved={() => updatePost(idx, 'image', '')}
                    siteBaseUrl={siteBaseUrl}
                    aspectRatio="aspect-[3/1]"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Summary / excerpt <span className="font-normal text-muted text-xs">(max 200)</span></Label>
                <Input value={post.excerpt || ''} onChange={(e) => updatePost(idx, 'excerpt', e.target.value)} maxLength={200}
                  placeholder="One or two sentences previewing the article — shown on the blog listing page" />
                <span className="text-xs text-muted">{(post.excerpt || '').length}/200 characters</span>
              </div>

              <div className="space-y-1.5">
                <Label>Article content</Label>
                <p className="text-xs text-muted">Write in plain text or Markdown. Minimum ~200 characters.</p>
                <Textarea rows={10} value={post.content || ''} onChange={(e) => updatePost(idx, 'content', e.target.value)}
                  className="font-mono text-sm" />
                <span className="text-xs text-muted">{(post.content || '').length} characters</span>
              </div>

              {/* SEO fields hidden under disclosure */}
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer list-none rounded-lg border border-zinc-200 px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors text-sm">
                  <svg className="w-3.5 h-3.5 text-muted transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
                  </svg>
                  <span className="font-medium text-primary">Advanced (SEO)</span>
                  <span className="ml-auto text-xs text-muted">Optional — controls Google search snippet</span>
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-4 px-1">
                  <div className="space-y-1.5">
                    <Label>SEO title <span className="font-normal text-muted text-xs">(max 60)</span></Label>
                    <Input value={post.metaTitle || ''} onChange={(e) => updatePost(idx, 'metaTitle', e.target.value)} maxLength={60}
                      placeholder="Defaults to post title if left blank" />
                    <span className="text-xs text-muted">{(post.metaTitle || '').length}/60</span>
                  </div>
                  <div className="space-y-1.5">
                    <Label>SEO description <span className="font-normal text-muted text-xs">(max 160)</span></Label>
                    <Input value={post.metaDescription || ''} onChange={(e) => updatePost(idx, 'metaDescription', e.target.value)} maxLength={160}
                      placeholder="Short description shown in Google results" />
                    <span className="text-xs text-muted">{(post.metaDescription || '').length}/160</span>
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PostsEditor;
